// ============================================================================
// Angebots-Zuordnung im Vario-Slot (listing-status.ts).
//
// Anlass (2026-09-13): Ein per Angebote-Tab gematchtes Angebot hat ein Mapping
// (vario.article_mappings) auf das Vario-Listing des Storefront-Kanals, aber keine Zeile im
// Lister-Posteingang (kaufland.listings) -- den schreiben nur Slot, MCP und Varianten-Anlage.
// Der Slot las bis dahin nur den Posteingang und bot deshalb „Neu listen / Aufschalten" an,
// obwohl der Artikel live bei Kaufland verkauft. Jetzt liefert `vario-listing-category`
// (load) das Mapping samt Unit als `matchedOffer` mit, und der Slot zeigt eine Karte
// „Aufgeschaltet ueber Angebots-Zuordnung" mit „Zuordnung aufheben".
//
// Hier die DOM-freien Teile: Anzeige-Entscheidung, Zeilen, Preisformat.
// ============================================================================

export interface MatchedOffer {
  idUnit: string;
  idOffer: string | null;
  idProduct: string | null;
  title: string | null;
  priceCents: number | null;
  currency: string | null;
  stock: number | null;
  status: string | null;
  assignedAt: string | null;
  varioArticleNumber: string | null;
  varioListingId: string | null;
}

/**
 * Die Karte erscheint nur im Wahl-Modus. Das Configure-Pane hat Vorrang: Ein ueber den Slot
 * aufgeschaltetes Listing hat NACH dem Push ebenfalls ein Mapping, dort ist aber der Lister
 * zustaendig. `forceChoice` (Kategorie aendern) schaltet auf die Wahl-Karten.
 */
export function zeigeMatchedKarte(i: { hatMatch: boolean; mode: string; showConfigure: boolean; forceChoice: boolean }): boolean {
  return i.hatMatch && i.mode === 'choice' && !i.showConfigure && !i.forceChoice;
}

export function formatPreis(cents: number | null | undefined, currency: string | null | undefined): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '—';
  const betrag = (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${betrag} ${currency}` : betrag;
}

const STATUS_LABEL: Readonly<Record<string, string>> = {
  active: 'aktiv',
  inactive: 'inaktiv',
  deleted: 'bei Kaufland gelöscht',
};

function oderStrich(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const s = String(v).trim();
  return s.length > 0 ? s : '—';
}

function formatZeitpunkt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Zeilen fuer die Karte, in Anzeige-Reihenfolge. Fehlende Werte als Strich, nie „null". */
export function matchedOfferZeilen(o: MatchedOffer): Array<{ label: string; wert: string }> {
  const status = o.status ? (STATUS_LABEL[o.status.toLowerCase()] ?? o.status) : '—';
  return [
    { label: 'Kaufland-Angebot', wert: oderStrich(o.idOffer) },
    { label: 'Kaufland-Produkt', wert: oderStrich(o.idProduct) },
    { label: 'Unit-ID', wert: oderStrich(o.idUnit) },
    { label: 'Titel bei Kaufland', wert: oderStrich(o.title) },
    { label: 'Preis', wert: formatPreis(o.priceCents, o.currency) },
    { label: 'Bestand bei Kaufland', wert: oderStrich(o.stock) },
    { label: 'Status', wert: status },
    { label: 'Zugeordnet am', wert: formatZeitpunkt(o.assignedAt) },
  ];
}
